import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import axios from "axios";
import TuneIcon from "@mui/icons-material/Tune";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import { Tooltip } from "@mui/material";
import DeviceDropDown from "./StatusDropdown.tsx";
import { toast } from "../../../components/Toast/toast.ts";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  width: {
    xs: "92%",
    sm: 450,
  },

  borderRadius: "8px",

  background: "rgb(var(--surface-1))",

  border: "1px solid rgb(var(--border-primary))",

  boxShadow: "var(--shadow-card-hover)",

  overflow: "hidden",

  outline: "none",

  backdropFilter: "blur(18px)",
};

export default function TriggerFilterModal({
  setData,
  setFilterVal,
  setLoading,
  loading,
  totalCount,
  setTotalCount,
  isAllDataFetched,
  setIsAllDataFetched,
  isInnerDataLoading,
  setInnerDataLoading,
  storevalue,
  setStoreValue,
  status,
  setStatus,
  todate,
  setTodate,
  fromdate,
  setFromdate,
  setCurrentCount,
  reload,
  setReload,
  fetchCountAndData,
  setCount,
  currentFilterCount,
  setCurrentFilterCount,
  searchVal,
  fetchFilteredData,
}: any) {
  const [open, setOpen] = React.useState(false);
  const [storename, setStoreName] = React.useState("");

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleFromDateChange = (newValue: any) => {
    setFromdate(newValue);

    if (newValue && todate && moment(newValue).isAfter(todate)) {
      setTodate(null);
    }
  };

  const handleToDateChange = (newValue: any) => {
    setTodate(newValue);

    if (newValue && fromdate && moment(fromdate).isAfter(newValue)) {
      setFromdate(null);
    }
  };

  const ResetFilter = async () => {
    try {
      setFilterVal(false);

      setStatus("");

      setFromdate(null);

      setTodate(null);

      setStoreValue("");

      setStoreName("");

      fetchCountAndData();

      setIsAllDataFetched(false);

      setCurrentCount(20);

      setCurrentFilterCount(20);
    } catch (error) {
      toast.error("Something went wrong!");
    }
  };

  return (
    <>
      {/* Trigger Button */}

      <Tooltip title="Filter Reports" arrow>
        <button
          onClick={handleOpen}
          className="
            flex
            items-center
            justify-center
            w-9
            h-9
            rounded-md
            transition-all
            duration-300
            border
            hover:scale-[1.03]
            active:scale-[0.98]
          "
          style={{
            background: "rgb(var(--surface-2))",
            borderColor: "rgb(var(--border-primary))",
            color: "rgb(var(--text-secondary))",
            boxShadow: "var(--shadow-sm)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor =
              "rgba(var(--color-gold-1),0.35)";

            e.currentTarget.style.background =
              "rgba(var(--color-gold-1),0.08)";

            e.currentTarget.style.color =
              "rgb(var(--color-gold-1))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor =
              "rgb(var(--border-primary))";

            e.currentTarget.style.background =
              "rgb(var(--surface-2))";

            e.currentTarget.style.color =
              "rgb(var(--text-secondary))";
          }}
        >
          <TuneIcon fontSize="small" />
        </button>
      </Tooltip>

      {/* Modal */}

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          {/* Header */}

          <div
            className="
              flex
              items-center
              justify-between
              px-6
              py-5
            "
            style={{
              background:
                "linear-gradient(180deg, rgba(var(--color-gold-1),0.08) 0%, transparent 100%)",

              borderBottom:
                "1px solid rgb(var(--border-primary))",
            }}
          >
            <div className="flex flex-col">
              <span
                className="
                  text-[18px]
                  font-semibold
                  tracking-tight
                "
                style={{
                  color: "rgb(var(--text-primary))",
                }}
              >
                Advanced Filters
              </span>

              <span
                className="text-[12px] mt-1"
                style={{
                  color: "rgb(var(--text-muted))",
                }}
              >
                Refine reports using date & status filters
              </span>
            </div>

            <div
              className="
                flex
                items-center
                justify-center
                w-11
                h-11
                rounded-2xl
              "
              style={{
                background:
                  "rgba(var(--color-gold-1),0.12)",

                border:
                  "1px solid rgba(var(--color-gold-1),0.18)",
              }}
            >
              <FilterAltRoundedIcon
                sx={{
                  color: "rgb(var(--color-gold-1))",
                  fontSize: 20,
                }}
              />
            </div>
          </div>

          {/* Body */}

          <div className="p-6">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              {/* Date Pickers */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* From Date */}

                <div className="flex flex-col gap-2">
                  <span
                    className="
                      text-[12px]
                      font-medium
                      uppercase
                      tracking-[0.08em]
                    "
                    style={{
                      color: "rgb(var(--text-muted))",
                    }}
                  >
                    From Date
                  </span>

                  <DatePicker
                    disableFuture
                    value={fromdate}
                    onChange={handleFromDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "52px",
                        borderRadius: "6px",
                        background:
                          "rgb(var(--surface-2))",
                        color:
                          "rgb(var(--text-primary))",
                        transition: "all 0.25s ease",
                      },

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgb(var(--border-primary))",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.35)",
                        },

                      "& .Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.45)",
                        },

                      "& .MuiInputLabel-root": {
                        color:
                          "rgb(var(--text-muted))",
                      },

                      "& .MuiInputBase-input": {
                        color:
                          "rgb(var(--text-primary))",

                        fontSize: "14px",
                      },

                      "& .MuiSvgIcon-root": {
                        color:
                          "rgb(var(--text-secondary))",
                      },
                    }}
                  />
                </div>

                {/* To Date */}

                <div className="flex flex-col gap-2">
                  <span
                    className="
                      text-[12px]
                      font-medium
                      uppercase
                      tracking-[0.08em]
                    "
                    style={{
                      color: "rgb(var(--text-muted))",
                    }}
                  >
                    To Date
                  </span>

                  <DatePicker
                    disableFuture
                    value={todate}
                    disabled={!fromdate}
                    minDate={fromdate}
                    onChange={handleToDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "52px",
                        borderRadius: "6px",
                        background:
                          "rgb(var(--surface-2))",
                        color:
                          "rgb(var(--text-primary))",
                        transition: "all 0.25s ease",
                      },

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgb(var(--border-primary))",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.35)",
                        },

                      "& .Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.45)",
                        },

                      "& .MuiInputLabel-root": {
                        color:
                          "rgb(var(--text-muted))",
                      },

                      "& .MuiInputBase-input": {
                        color:
                          "rgb(var(--text-primary))",

                        fontSize: "14px",
                      },

                      "& .MuiSvgIcon-root": {
                        color:
                          "rgb(var(--text-secondary))",
                      },
                    }}
                  />
                </div>
              </div>

              {/* Status */}

              <div className="mt-5 flex flex-col gap-2">
                <span
                  className="
                    text-[12px]
                    font-medium
                    uppercase
                    tracking-[0.08em]
                  "
                  style={{
                    color: "rgb(var(--text-muted))",
                  }}
                >
                  Device Status
                </span>

                <div
                  className="rounded-[18px]"
                  style={{
                    background: "rgb(var(--surface-2))",
                  }}
                >
                  <DeviceDropDown
                    status={status}
                    setStatus={setStatus}
                  />
                </div>
              </div>

              {/* Selected Filter Summary */}

              {(fromdate || todate || status) && (
                <div
                  className="
                    mt-5
                    px-4
                    py-3
                    rounded-2xl
                    flex
                    flex-col
                    gap-2
                  "
                  style={{
                    background:
                      "rgba(var(--color-gold-1),0.08)",

                    border:
                      "1px solid rgba(var(--color-gold-1),0.15)",
                  }}
                >
                  <span
                    className="
                      text-[11px]
                      uppercase
                      tracking-[0.08em]
                    "
                    style={{
                      color:
                        "rgb(var(--text-muted))",
                    }}
                  >
                    Active Filters
                  </span>

                  <div className="flex flex-wrap gap-2">
                    {fromdate && (
                      <div
                        className="
                          px-3
                          py-1.5
                          rounded-full
                          text-[12px]
                          font-medium
                        "
                        style={{
                          background:
                            "rgba(var(--color-gold-1),0.12)",

                          color:
                            "rgb(var(--text-primary))",

                          border:
                            "1px solid rgba(var(--color-gold-1),0.15)",
                        }}
                      >
                        From:{" "}
                        {moment(fromdate).format(
                          "DD MMM YYYY"
                        )}
                      </div>
                    )}

                    {todate && (
                      <div
                        className="
                          px-3
                          py-1.5
                          rounded-full
                          text-[12px]
                          font-medium
                        "
                        style={{
                          background:
                            "rgba(var(--color-gold-1),0.12)",

                          color:
                            "rgb(var(--text-primary))",

                          border:
                            "1px solid rgba(var(--color-gold-1),0.15)",
                        }}
                      >
                        To:{" "}
                        {moment(todate).format(
                          "DD MMM YYYY"
                        )}
                      </div>
                    )}

                    {status && (
                      <div
                        className="
                          px-3
                          py-1.5
                          rounded-full
                          text-[12px]
                          font-medium
                        "
                        style={{
                          background:
                            "rgba(var(--color-gold-1),0.12)",

                          color:
                            "rgb(var(--text-primary))",

                          border:
                            "1px solid rgba(var(--color-gold-1),0.15)",
                        }}
                      >
                        Status: {status}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Buttons */}

              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  marginTop: "28px",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={() => {
                    ResetFilter();
                    handleClose();
                  }}
                  sx={{
                    height: "36px",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    paddingInline: "18px",
                    background:
                      "rgb(var(--surface-2))",
                    color:
                      "rgb(var(--text-primary))",
                    border:
                      "1px solid rgb(var(--border-primary))",
                    boxShadow: "none",

                    "&:hover": {
                      background:
                        "rgba(239,68,68,0.08)",
                      borderColor:
                        "rgba(239,68,68,0.25)",
                      color: "#ef4444",
                      boxShadow: "none",
                    },
                  }}
                >
                  Reset
                </Button>

                <Button
                  variant="contained"
                  startIcon={<CheckRoundedIcon />}
                  onClick={() => {
                    fetchFilteredData();
                    handleClose();
                  }}
                  disabled={
                    !(
                      storevalue ||
                      todate ||
                      fromdate ||
                      status
                    )
                  }
                  sx={{
                    height: "36px",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    paddingInline: "22px",
                    background:
                      "var(--gold-grad)",
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      background:
                        "rgb(var(--color-gold-2))",

                      boxShadow:
                        "0 10px 30px rgba(var(--color-gold-1),0.25)",
                    },
                  }}
                >
                  Apply Filters
                </Button>
              </Stack>
            </LocalizationProvider>
          </div>
        </Box>
      </Modal>
    </>
  );
}